// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AddRounded } from '@/constants/hardfin-standard-icons';
import { PROJECT_STATUS_LABELS } from '@/constants/project-status';
import { SparseProject } from '@/sparse/api/types';
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { omit } from 'lodash-es';
import { ForwardedRef, HTMLAttributes, forwardRef, useMemo } from 'react';

/**
 * Special ID used to identify the "create new project" option in the dropdown.
 * This distinguishes it from regular project options for special handling.
 */
export const CREATE_PROJECT_OPTION_ID = 'create-project';

/**
 * Determines if the option is equal to the value.
 *
 * @param option - A project option to compare.
 * @param value - A project value to compare.
 */
function isOptionEqualToValue(option: SparseProject, value: SparseProject) {
  return option.id === value.id;
}

/**
 * Returns the label of the option.
 *
 * @param option - A project option to get the label from.
 */
function getOptionLabel(option: SparseProject) {
  return option.name;
}

/**
 * Builds the per-option renderer. When `showStatusChip` is on, each row
 * shows the project name on the left and a small filled status chip on
 * the right (mirrors the customer/supplier chip on the company picker).
 */
function buildRenderOption(showStatusChip: boolean) {
  return function renderOption(
    attributes: HTMLAttributes<HTMLLIElement>,
    option: SparseProject
  ) {
    // Ensure that 'key' is not spread onto the list item element.
    const props = omit(attributes, 'key');

    // Special rendering for "create new project" option
    if (option.id === CREATE_PROJECT_OPTION_ID) {
      return (
        <li {...props} key={option.id}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AddRounded fontSize="small" color="primary" />
            <Typography color="primary.main">
              Add project <b>{option.name}</b>
            </Typography>
          </Stack>
        </li>
      );
    }

    const color = option.isArchived ? 'text.secondary' : 'text.primary';

    if (!showStatusChip) {
      return (
        <li {...props} key={option.id}>
          <Typography color={color}>
            {option.name} {option.isArchived && '(Archived)'}
          </Typography>
        </li>
      );
    }

    return (
      <li {...props} key={option.id}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          width="100%"
          justifyContent="space-between"
        >
          <Typography color={color}>
            {option.name} {option.isArchived && '(Archived)'}
          </Typography>
          <Chip
            label={PROJECT_STATUS_LABELS[option.status]}
            size="small"
            color="secondary"
            variant="filled"
          />
        </Stack>
      </li>
    );
  };
}

interface Props<
  Option extends SparseProject,
  Multiple extends boolean | undefined,
> extends Omit<
    AutocompleteProps<Option, Multiple, false, false>,
    | 'renderInput'
    | 'isOptionEqualToValue'
    | 'getOptionLabel'
    | 'renderOption'
    | 'groupBy'
  > {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * If provided, the placeholder text will be displayed in the input element.
   */
  placeholder?: string;

  /**
   * If provided, used to determine the string value for a given option.
   * It's used to group options together.
   */
  groupBy?: (option: Option) => string;

  /**
   * When true, each option renders a right-aligned status chip
   * (Active / Upcoming / Past / No dates set). Defaults to false to keep
   * the existing single-line layout for pickers that don't need it.
   */
  showStatusChip?: boolean;
}

/**
 * Renders a project autocomplete component and forwards a ref to the input
 * element.
 */
function ProjectAutocompleteRenderer<
  Option extends SparseProject,
  Multiple extends boolean | undefined,
>(props: Props<Option, Multiple>, ref: ForwardedRef<HTMLInputElement>) {
  const {
    name,
    label,
    placeholder,
    error,
    helperText,
    groupBy,
    showStatusChip,
    ...autocompleteProps
  } = props;

  const renderInput = (parameters: AutocompleteRenderInputParams) => {
    return (
      <TextField
        inputRef={ref}
        name={name}
        label={label}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        {...parameters}
      />
    );
  };

  const renderOption = useMemo(
    () => buildRenderOption(showStatusChip ?? false),
    [showStatusChip]
  );

  return (
    <Autocomplete
      {...autocompleteProps}
      groupBy={groupBy}
      renderInput={renderInput}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      filterSelectedOptions={props.multiple}
      isOptionEqualToValue={isOptionEqualToValue}
    />
  );
}

/**
 * An autocomplete component for selecting a project from a list of options.
 */
export const ProjectAutocomplete = forwardRef(ProjectAutocompleteRenderer);
